import {
  IBenefitPreviewResult,
  IBenefitSelectionExplanation,
  IBenefitSelectionHistory,
  IBenefitSelectionSaveRequest,
  IBenefitSelectionSaveResult,
  IBenefitSelectionView,
  IExplainCheck,
} from "types/BenefitSelection/BenefitSelection";

import { api } from ".";

/**
 * CLE-2792 — one account's benefit selection configuration.
 *
 * Three of these four endpoints are PR 1 (`GET`/`PUT .../benefitSelection`,
 * `PUT .../benefitSelection/engine`) and the explainer is PR 1b. `previewBenefitSelection` is
 * declared against the contract PR 3 is being built to and is the ONLY endpoint here that does not
 * exist on the server yet — the dialog that calls it says so on screen rather than failing silently.
 *
 * Every mutation invalidates BOTH `BenefitSelection` and `Account`: the save writes payer filters,
 * which the account edit form re-seeds itself from, so a stale account cache would put the pre-save
 * filter list back into the form and the next account save would write it back.
 */
const benefitSelectionQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getBenefitSelection: builder.query<
      { data: IBenefitSelectionView; message: string },
      string
    >({
      query: (accountId: string) => `accounts/${accountId}/benefitSelection`,
      providesTags: (_result, _error, accountId) => [
        { type: "BenefitSelection" as const, id: accountId },
      ],
    }),
    getBenefitSelectionExplain: builder.query<
      { data: IBenefitSelectionExplanation; message: string },
      string
    >({
      query: (accountId: string) =>
        `accounts/${accountId}/benefitSelection/explain`,
      providesTags: (_result, _error, accountId) => [
        { type: "BenefitSelection" as const, id: `${accountId}-explain` },
      ],
    }),
    /**
     * Every saved change to this account's configuration, newest first. An empty `changes` array is
     * a real answer — "nobody has saved anything yet" — and is NOT the same as the endpoint being
     * absent, which is why the drawer reads the array rather than the query's error state.
     */
    getBenefitSelectionHistory: builder.query<
      { data: IBenefitSelectionHistory; message: string },
      string
    >({
      query: (accountId: string) =>
        `accounts/${accountId}/benefitSelection/history`,
      providesTags: (_result, _error, accountId) => [
        { type: "BenefitSelection" as const, id: `${accountId}-history` },
      ],
    }),
    /**
     * 🚨 THE EXPLANATION OF A CONFIGURATION NOBODY HAS SAVED YET. `GET .../explain` answers for what
     * is in force, which is the wrong answer while somebody is deciding whether to save: they change
     * the service line, open "How it is selected", and read the steps for the line they just moved
     * away from. This takes the PUT's own body and returns the GET's own shape, so the panel can
     * describe the screen instead of the database without composing a word of it here.
     */
    explainBenefitSelectionDraft: builder.mutation<
      { data: IBenefitSelectionExplanation; message: string },
      { accountId: string; body: IBenefitSelectionSaveRequest }
    >({
      query: ({ accountId, body }) => ({
        url: `accounts/${accountId}/benefitSelection/explain`,
        method: "POST",
        body,
      }),
    }),
    /**
     * ONE CHECK, EXPLAINED — what this configuration made of a pVerify response the account has
     * already run. It carries the unsaved configuration when there is one, for the same reason the
     * draft explainer does: somebody checking a patient's copay against an edit they have not saved
     * needs the answer the edit produces, not the answer the database still holds.
     *
     * A mutation rather than a query because it POSTs a body and must re-run on demand; nothing is
     * invalidated, because nothing was written.
     */
    explainBenefitCheck: builder.mutation<
      { data: IExplainCheck; message: string },
      {
        accountId: string;
        pverifyRequestId: string;
        configuration?: IBenefitSelectionSaveRequest;
      }
    >({
      query: ({ accountId, pverifyRequestId, configuration }) => ({
        url: `accounts/${accountId}/benefitSelection/explain/check`,
        method: "POST",
        body: configuration
          ? { pverifyRequestId, configuration }
          : { pverifyRequestId },
      }),
    }),
    saveBenefitSelection: builder.mutation<
      { data: IBenefitSelectionSaveResult; message: string },
      { accountId: string; body: IBenefitSelectionSaveRequest }
    >({
      query: ({ accountId, body }) => ({
        url: `accounts/${accountId}/benefitSelection`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { accountId }) => [
        { type: "BenefitSelection" as const, id: accountId },
        { type: "BenefitSelection" as const, id: `${accountId}-explain` },
        { type: "BenefitSelection" as const, id: `${accountId}-history` },
        { type: "Account" as const, id: accountId },
      ],
    }),
    setBenefitEngineEnabled: builder.mutation<
      { data: IBenefitSelectionView["engine"]; message: string },
      { accountId: string; enabled: boolean }
    >({
      query: ({ accountId, enabled }) => ({
        url: `accounts/${accountId}/benefitSelection/engine`,
        method: "PUT",
        body: { enabled },
      }),
      invalidatesTags: (_result, _error, { accountId }) => [
        { type: "BenefitSelection" as const, id: accountId },
        { type: "BenefitSelection" as const, id: `${accountId}-history` },
        { type: "Account" as const, id: accountId },
      ],
    }),
    /**
     * 🚨 PR 3 BUILDS THIS. The dialog is written against the agreed contract and shipped disabled-
     * looking rather than fabricated: until PR 3 merges the call 404s and the dialog says the
     * preview is not available yet.
     */
    previewBenefitSelection: builder.mutation<
      { data: IBenefitPreviewResult; message: string },
      { accountId: string; body: IBenefitSelectionSaveRequest }
    >({
      query: ({ accountId, body }) => ({
        url: `accounts/${accountId}/benefitSelection/preview`,
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useExplainBenefitCheckMutation,
  useGetBenefitSelectionQuery,
  useExplainBenefitSelectionDraftMutation,
  useGetBenefitSelectionExplainQuery,
  useGetBenefitSelectionHistoryQuery,
  useSaveBenefitSelectionMutation,
  useSetBenefitEngineEnabledMutation,
  usePreviewBenefitSelectionMutation,
} = benefitSelectionQueries;
